// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}


d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});


// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

const margins = {
  top: 30,
  right: 80,
  bottom: 25,
  left: 40,
};

const chartData = [];
const minValue = 0;
let maxValue = 150; // Changed to let to allow reassignment
const minDomain = new Date(2000, 0, 1); // normalize to year 2000 to plot multiple years on same axis
const maxDomain = new Date(2000, 11, 31); // javascript months are zero-indexed
let latestData;
let timestamp;
let compareText;
let updated_date;


d3.json('https://ix.cnn.io/data/measles/toplines_measles_cases.json').then((summary) => {
  timestamp = summary[0].updated_date;
  compareText = summary[0].compare_text;
  const lastUpdatedStr = summary[0].last_updated_str;

  d3.select('span#timestamp')
    .text(`Data updated on ${timestamp}`);

  d3.select('span#updated_date')
    .text(`${timestamp}`);

  d3.select('span#compare')
    .html(`${compareText}`);

  d3.select('span#last_updated')
    .text(`${lastUpdatedStr}`);
});


d3.json('https://ix.cnn.io/data/measles/weekly_measles_cases.json').then((data) => {
  const transitionData = {};

  data.forEach((d) => {
    const { year } = d;
    // Filter out 2022 to reduce label crowding
    if (year == 2022) return;

    if (!(year in transitionData)) {
      transitionData[year] = [];
    }


    // Parse the actual week_start date
    const weekStart = d3.timeParse('%Y-%m-%d')(d.week_start);
    // Normalize to year 2000 using the actual date's month and day
    const normalizedDate = new Date(2000, weekStart.getMonth(), weekStart.getDate());

    transitionData[year].push({
      count: parseInt(d.cases_cumsum),
      date: normalizedDate,
      weekStart,
    });
  });

  for (const key in transitionData) {
    const yearData = transitionData[key];

    // Check if the first data point is after January 2
    // If so, add a starting point at Jan 1 with 0 cases
    // This handles cases where week 1 is assigned to the previous year
    // or when data collection starts mid-year
    if (yearData.length > 0) {
      const firstDate = yearData[0].date;
      const jan2 = new Date(2000, 0, 2); // Jan 2 in normalized year

      if (firstDate > jan2) {
        const startDate = new Date(2000, 0, 1); // Jan 1 in normalized year
        yearData.unshift({
          count: 0,
          date: startDate,
          weekStart: new Date(parseInt(key), 0, 1),
        });
      }
    }

    chartData.push({
      year: key,
      data: yearData,
    });
  }


  // const latestYear = chartData.filter(d => d.year == '2024')[0];
  const latestYear = chartData.reduce((max, d) => (parseInt(d.year) > parseInt(max.year) ? d : max), chartData[0]);
  latestData = latestYear.data[latestYear.data.length - 1];


  const localMax = d3.max(chartData, d => d3.max(d.data, e => e.count));
  if (localMax > maxValue) {
    maxValue = localMax;
  }

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const latestYear = chartData.reduce((max, d) => (parseInt(d.year) > parseInt(max.year) ? d : max), chartData[0]);

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const sortedYears = chartData.map(d => parseInt(d.year)).sort((a, b) => b - a);

  // Get years (excluding the latest year)
  const prevYears = sortedYears.slice(1);
  const currentYear = sortedYears[0];

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(4)
    .tickFormat(d => d3.timeFormat('%b')(d));

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .tickSize(-chartWidth)
    .ticks(5)
    .tickFormat(d => d3.format(',')(d));

  const path = d3.line()
    .curve(d3.curveLinear)
    .x(d => xScale(d.date))
    .y(d => yScale(d.count));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  d3.select('.grid.y .domain').remove();


  const line = g.selectAll('.trend')
    .data(chartData)
    .enter()
    .append('g')
    .attr('class', d => `trend trend-${d.year} ${d.year == currentYear ? 'trend-latest' : ''}`);

  line.append('path')
    .datum(d => d.data)
    .attr('d', path);

  const annotation = g.append('g')
    .attr('class', 'annotation-2024');

  annotation.append('circle')
    .attr('cx', xScale(latestData.date))
    .attr('cy', yScale(latestData.count))
    .attr('r', '5')
    .attr('fill', '#7c4ea5')
    .attr('stroke', 'none');

  // Short leader line from the data point down to the label
  const leaderLength = 36;
  const midY = Math.min(yScale(latestData.count) + leaderLength, chartHeight - 30);
  annotation.append('line')
    .attr('x1', xScale(latestData.date))
    .attr('y1', yScale(latestData.count) + 5) // start at the edge of the circle
    .attr('x2', xScale(latestData.date))
    .attr('y2', midY)
    .attr('stroke', '#7c4ea5')
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', '2,2');

  // Position annotation midway through the chart, offset slightly to the right
  const annotationText = annotation.append('g')
    .attr('transform', `translate(${[xScale(latestData.date) + 8, midY]})`);

  annotationText.append('text')
    .attr('dy', '0')
    .attr('dx', '0')
    .attr('text-anchor', 'start')
    .attr('class', 'annotation-label')
    .text(`${latestYear.year}`);

  annotationText.append('text')
    .attr('dy', '18')
    .attr('dx', '0')
    .attr('text-anchor', 'start')
    .attr('class', 'annotation-value')
    .text(`${d3.format(',')(latestData.count)} cases`);

  // Previous-year labels: start at each line's end value, then resolve overlaps
  const labelGap = 32; // min px between label baselines (year + cases = two lines)
  const prevLabels = prevYears
    .map((year) => {
      const prevYearData = chartData.find(d => d.year == year);
      if (!prevYearData) return null; // Skip if data is missing
      const lastPoint = prevYearData.data[prevYearData.data.length - 1];
      return {
        year,
        lastPoint,
        y: yScale(lastPoint.count),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.y - a.y); // bottom-most first

  // Anchor the lowest label (nearest the axis) and push the others up
  for (let i = 1; i < prevLabels.length; i++) {
    prevLabels[i].y = Math.min(prevLabels[i].y, prevLabels[i - 1].y - labelGap);
  }

  prevLabels.forEach(({ year, lastPoint, y }) => {
    const annotationPrev = g.append('g')
      .attr('class', 'annotation-prev')
      .attr('transform', `translate(${[xScale(lastPoint.date) + 5, y]})`);

    annotationPrev.append('text')
      .attr('class', 'annotation-prev-text')
      .attr('text-anchor', 'start')
      .text(year);

    annotationPrev.append('text')
      .attr('class', 'annotation-value-line2')
      .attr('text-anchor', 'start')
      .attr('dy', '1.2em')
      .text(`${d3.format(',')(lastPoint.count)} cases`);
  });

  pymChild.sendHeight();
}
